<template>
    <DsInlineAlert
        class="recurring-alert"
        :leading-icon="icon"
        type="subtle"
    >
        <template #default>
            <i18n-t
                v-if="item.recurringBilling"
                :keypath="recurringPaymentMessage"
                tag="span"
                data-qa="recurring-message"
            >
                <template #name>
                    {{ name }}
                </template>

                <template v-if="item.billingCycle === 2" #the>
                    {{ $t('RecurringItemDescription.paymentSchedule.the') }}
                </template>

                <template #boldFrequency>
                    <span class="semibold">
                        {{ recurringFrequencyMessage(startDate) }}
                    </span>
                </template>

                <template #boldStartDate>
                    <span class="semibold">{{ formattedStartDate }}</span>
                </template>

                <template v-if="item.frequency !== 0 && item.numberOfPayments > 0" #occurrences>
                    {{ occurrences }}
                </template>

                <template v-if="showEndingMessage" #endingMessage>
                    {{ endingMessage }}
                </template>

                <template v-if="showEndingDate" #boldEndDate>
                    <span class="semibold">{{ endDate }}</span>
                </template>

                <template v-if="showStartDateUpdatedMessage" #startDateUpdatedBegin>
                    {{ $t('RecurringItemDescription.paymentSchedule.startDateUpdatedBegin') }}
                </template>

                <template v-if="showStartDateUpdatedMessage" #immediately>
                    <span class="semibold">{{ $t('RecurringItemDescription.paymentSchedule.immediately') }}</span>
                </template>

                <template v-if="showStartDateUpdatedMessage" #startDateUpdatedEnd>
                    {{ $t('RecurringItemDescription.paymentSchedule.startDateUpdatedEnd') }}
                </template>
            </i18n-t>

            <i18n-t
                v-else
                keypath="RecurringItemDescription.oneTimePayment.message"
                tag="span"
                data-qa="one-time-message"
            >
                <template #name>
                    {{ name }}
                </template>

                <template #boldCharged>
                    <span class="semibold">
                        {{ $t('RecurringItemDescription.oneTimePayment.boldCharged') }}
                    </span>
                </template>

                <template #oneOrMoreItems>
                    {{ $tc('RecurringItemDescription.oneTimePayment.oneOrMoreItems', item.quantity) }}
                </template>

                <template #boldDue>
                    <span class="semibold">
                        {{ $t('oneTimePayment.boldDue') }}
                    </span>
                </template>
            </i18n-t>
        </template>

        <template v-if="editing" #trailing>
            <DsFilledButton
                gray
                dense
                data-qa="edit-button"
                @click="$emit('editLineItem', item)"
            >
                {{ $t('global.edit') }}
            </DsFilledButton>
        </template>
    </DsInlineAlert>
</template>

<script>
import moment from 'moment';
import { DsInlineAlert, DsFilledButton } from '@infusionsoft/design-system';
import {
    DISPLAY_DATE_FORMAT_WITH_YEAR,
    DISPLAY_DATE_ORDINAL,
    DISPLAY_DATE_DAY_LONG,
    DISPLAY_SHORT_DATE_FORMAT,
} from '@keap-web/shared-ui';
import invoiceLineItemsMixin from '../mixins/invoice-line-items';
import { billingCycles } from '../money.constants';
import { timeUnitStatus } from '../money.util';

export default {
    components: {
        DsInlineAlert,
        DsFilledButton,
    },

    mixins: [invoiceLineItemsMixin],

    props: {
        item: {
            type: Object,
            required: true,
        },
        editing: Boolean,
        subjectIsItem: Boolean,
        contact: {
            type: String,
            default: '',
        },
        invoiceStartDate: {
            type: [String, Date],
            default: '',
        },
    },

    emits: ['editLineItem'],

    computed: {
        startDate() {
            return this.item.recurringStartDate ?? this.item.startDate ?? this.invoiceStartDate;
        },

        icon() {
            return this.item?.recurringBilling ? 'calendar' : 'receipt';
        },

        name() {
            return this.editing ? this.contact : this.$t('RecurringItemDescription.you');
        },

        isToday() {
            return moment().isSame(this.startDate, 'day');
        },

        occurrences() {
            const { numberOfPayments, billingCycle } = this.item;

            return `${this.$t('RecurringItemDescription.orderItem.for')} ${numberOfPayments} ${this.$tc(`RecurringItemDescription.timeInterval.${billingCycle}`, numberOfPayments)},`;
        },

        recurringPaymentMessage() {
            if (this.subjectIsItem) {
                return this.item.billingCycle === billingCycles.DAILY
                    ? 'RecurringItemDescription.itemRecurringDailyMessage'
                    : 'RecurringItemDescription.itemRecurringMessage';
            }

            return this.item.billingCycle === billingCycles.DAILY
                ? 'RecurringItemDescription.recurringDailyMessage'
                : 'RecurringItemDescription.recurringPaymentMessage';
        },

        formattedStartDate() {
            return moment.utc(this.startDate).format(DISPLAY_DATE_FORMAT_WITH_YEAR);
        },

        showEndingMessage() {
            return this.item?.numberOfPayments || this.item?.recurringNextBillDate;
        },

        endingMessage() {
            if (!this.item?.recurringNextBillDate) return this.$t('RecurringItemDescription.paymentSchedule.endingMessage');

            const paymentsMadeMessageText = this.editing ? 'RecurringItemDescription.paymentSchedule.editingPaymentsMadeMessage' : 'RecurringItemDescription.paymentSchedule.paymentsMadeMessage';
            const paymentsMadeMessage = this.$tc(paymentsMadeMessageText, this.item.recurringCyclesCompleted, { paymentsMade: this.item.recurringCyclesCompleted });
            const paymentsRemaining = this.invoiceLineItems_remainingRecurringCycles;

            if (this.item.recurringEndDate && paymentsRemaining === 0) return `${paymentsMadeMessage} ${this.$t('RecurringItemDescription.paymentSchedule.completedMessage')}`;
            if (this.item.recurringInactive) return `${paymentsMadeMessage} ${this.$t('RecurringItemDescription.paymentSchedule.inactiveMessage')}`;
            if (this.item.recurringEndDate) return `${paymentsMadeMessage} ${this.$tc('RecurringItemDescription.paymentSchedule.paymentsRemainingMessage', paymentsRemaining, { paymentsRemaining })}`;

            return paymentsMadeMessage;
        },

        showEndingDate() {
            return (!this.item?.recurringNextBillDate && this.item?.numberOfPayments) || (this.item?.recurringEndDate && (this.invoiceLineItems_remainingRecurringCycles === 0 || !this.item?.recurringInactive));
        },

        endDate() {
            const timeUnit = timeUnitStatus(`${this.item.billingCycle}`);
            const endDate = this.item?.recurringEndDate
                ? this.invoiceLineItems_adjustRecurringEndDate
                : moment(this.invoiceLineItems_invoiceEndDate).add(1, timeUnit);

            return moment.utc(endDate).format(DISPLAY_DATE_FORMAT_WITH_YEAR);
        },

        showStartDateUpdatedMessage() {
            return !(this.isToday || this.item?.recurringNextBillDate);
        },
    },

    methods: {
        recurringFrequencyMessage(date) {
            let day;

            switch (this.item.billingCycle) {
            case billingCycles.YEARLY:
                day = moment.utc(date).format(DISPLAY_SHORT_DATE_FORMAT);
                break;
            case billingCycles.MONTHLY:
                day = moment.utc(date).format(DISPLAY_DATE_ORDINAL);
                break;
            case billingCycles.WEEKLY:
                day = moment.utc(date).format(DISPLAY_DATE_DAY_LONG);
                break;
            default:
                day = '';
                break;
            }

            return this.item.billingCycle !== billingCycles.DAILY
                ? this.$t('RecurringItemDescription.paymentSchedule.boldFrequency', { day, frequency: this.$tc(`RecurringItemDescription.timeInterval.${this.item.billingCycle}`, 1) })
                : this.$t('RecurringItemDescription.paymentSchedule.boldEveryDay');
        },
    },
};

</script>

<style lang="scss" scoped>
@import '../styles/common';

.recurring-alert {
    width: 100%;
    display: grid;
    grid-template-columns: auto 1fr auto;
    margin-top: $spacing-200;
}
</style>
